import React, { useEffect } from "react";
import { connect } from "react-redux";

import BoundaryType from "@mapmycustomers/shared/enum/BoundaryType";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import type { Territory } from "@mapmycustomers/shared/types/entity";
import type { SimpleCondition } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";

import ExtendedAnalytics from "@app/component/analytics/ExtendedAnalytics";
import IMapViewContext from "@app/component/view/types/IMapViewContext";
import FilterBar from "@app/scene/map/components/MapMode/components/FilterBar";
import { MapLayer } from "@app/scene/map/store";
import { setBoundaryType, toggleLayerVisibility } from "@app/scene/map/store/layers/actions";
import withMapView from "@app/scene/map/utils/withMapView";
import { showTerritories } from "@app/store/territories/actions";
import { UniversalFieldName } from "@app/util/fieldModel/universalFieldsFieldModel";
import { isSimpleCondition } from "@app/util/viewModel/assert";

import TopCommandsBar from "./TopCommandsBar";
import styles from "./TopPanel.module.scss";

interface Props extends IMapViewContext {
  onBoundaryTypeChange: typeof setBoundaryType;
  onShowTerritories?: (visibleTerritoryIds: Territory["id"][]) => void;
  onToggleLayerVisibility?: (payload: { name: MapLayer; visible: boolean }) => void;
}

const TopPanel: React.FC<Props> = ({
  onBoundaryTypeChange,
  onShowTerritories,
  onToggleLayerVisibility,
  viewState: { filter, tool },
}) => {
  useEffect(() => {
    if (
      !filter.universal?.[UniversalFieldName.TERRITORIES] ||
      !isSimpleCondition(filter.universal[UniversalFieldName.TERRITORIES])
    ) {
      return; // nothing to do here
    }

    // show territories layer when user tries to filter by them
    const { operator, value } = filter.universal[UniversalFieldName.TERRITORIES] as SimpleCondition;
    if (operator !== FilterOperator.NOT_IN) {
      onShowTerritories?.(value as Territory["id"][]);
      onToggleLayerVisibility?.({ name: "boundaries", visible: true });
      onBoundaryTypeChange(BoundaryType.TERRITORY);
    }
  }, [filter, onBoundaryTypeChange, onShowTerritories, onToggleLayerVisibility]);

  return tool ? null : (
    <div className={styles.container}>
      <ExtendedAnalytics keyPath="Filters">
        <FilterBar />
      </ExtendedAnalytics>
      <TopCommandsBar />
    </div>
  );
};

const mapDispatchToProps = {
  onBoundaryTypeChange: setBoundaryType,
  onShowTerritories: showTerritories,
  onToggleLayerVisibility: toggleLayerVisibility,
};

export default connect(null, mapDispatchToProps)(withMapView(TopPanel, ["filter", "tool"]));
